import { useEffect, useMemo } from "react";
import { Box, Dialog, DialogContent, Stack, TextField, useTheme } from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Button, FoodsIconButton, FoodsSelect, Typography } from "@foodpilot/foods";
import { useSnackbar } from "src/utils/useSnackbar";
import {
    CohortAttributeChoice,
    CohortDefinition,
    CohortDefinitionSchema,
    CohortDefinitionSourceSchema,
    CohortDefinitionWrite,
    useCohortAttributeChoices,
    useCreateOrUpdateCohortDefinition,
} from "src/api/client-api/cohorts/useCohortDefinitions";

const EntityClassSchema = CohortDefinitionSchema.shape.entityClass;

type EntityClass = z.infer<typeof EntityClassSchema>;
type CohortSource = z.infer<typeof CohortDefinitionSourceSchema>;

type SelectOption<T extends string> = {
    value: T;
    label: string;
};

type CohortDefinitionModalProps = {
    open: boolean;
    onClose: () => void;
    definition?: CohortDefinition;
    onSuccess?: () => void;
};

export const CohortDefinitionModal = (props: CohortDefinitionModalProps) => {
    const { open, onClose, definition, onSuccess } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const snackbar = useSnackbar();
    const mutation = useCreateOrUpdateCohortDefinition();

    const formSchema = useMemo(
        () =>
            z.object({
                id: z.number().optional(),
                code: z
                    .string()
                    .min(1, t("settings.cohorts.validation.code_required", "Code is required"))
                    .regex(
                        /^[a-z][a-z0-9_]*$/,
                        t(
                            "settings.cohorts.validation.code_format",
                            "Code must start with a letter and use lowercase letters, numbers, or underscores.",
                        ),
                    ),
                label: z.string().min(1, t("settings.cohorts.validation.label_required", "Label is required")),
                entityClass: EntityClassSchema,
                source: CohortDefinitionSourceSchema,
                attributeKey: z
                    .string()
                    .min(1, t("settings.cohorts.validation.attribute_required", "Attribute is required")),
                minimumPeerCount: z
                    .number()
                    .int()
                    .positive(
                        t(
                            "settings.cohorts.validation.minimum_peer_count_positive",
                            "Minimum peer count must be positive",
                        ),
                    ),
            }),
        [t],
    );

    type CohortDefinitionFormData = z.infer<typeof formSchema>;

    const entityClassOptions: SelectOption<EntityClass>[] = [
        { value: "Organization", label: t("settings.cohorts.entity_class.organization", "Organization") },
        { value: "Product", label: t("entity.product", "Product") },
        { value: "Ingredient", label: t("entity.ingredient", "Ingredient") },
        { value: "Packaging", label: t("entity.packaging", "Packaging") },
    ];

    const sourceOptions: SelectOption<CohortSource>[] = [
        { value: "builtin", label: t("settings.cohorts.source.builtin", "Built-in field") },
        { value: "json", label: t("settings.cohorts.source.json", "Custom JSON attribute") },
    ];

    const selectTextOptions = {
        placeholder: t("component.select.placeholder"),
        searchPlaceholder: t("component.select.searchplaceholder"),
        emptyOptionsText: t("component.select.nooptions"),
        defaultValueText: t("component.select.defaultvalue"),
        noResult: t("component.select.noresultfound"),
    };

    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
        reset,
        watch,
        setValue,
    } = useForm<CohortDefinitionFormData>({
        mode: "onChange",
        resolver: zodResolver(formSchema),
        defaultValues: {
            code: "",
            label: "",
            entityClass: "Organization",
            source: "builtin",
            attributeKey: "",
            minimumPeerCount: 10,
        },
    });

    const entityClass = watch("entityClass");
    const { attributeChoices, isLoading: isLoadingChoices } = useCohortAttributeChoices(entityClass);

    useEffect(() => {
        if (!open) return;

        if (definition) {
            reset({
                id: definition.id,
                code: definition.code,
                label: definition.label,
                entityClass: definition.entityClass,
                source: definition.source,
                attributeKey: definition.attributeKey,
                minimumPeerCount: definition.minimumPeerCount,
            });
            return;
        }

        reset({
            code: "",
            label: "",
            entityClass: "Organization",
            source: "builtin",
            attributeKey: "",
            minimumPeerCount: 10,
        });
    }, [open, definition, reset]);

    useEffect(() => {
        if (!open || definition) return;
        setValue("attributeKey", "", { shouldValidate: true });
    }, [entityClass, open, definition, setValue]);

    const onSubmit: SubmitHandler<CohortDefinitionFormData> = async (data) => {
        const payload: CohortDefinitionWrite = {
            id: data.id,
            code: data.code,
            label: data.label,
            entityClass: data.entityClass,
            source: data.source,
            attributeKey: data.attributeKey,
            minimumPeerCount: data.minimumPeerCount,
        };

        try {
            await mutation.mutateAsync(payload);
            snackbar.setSnackbarProps({
                message: t("settings.cohorts.save.success", "Cohort definition saved"),
                type: "success",
            });
            onSuccess?.();
            onClose();
        } catch {
            snackbar.setSnackbarProps({
                message: t("settings.cohorts.save.error", "Failed to save cohort definition"),
                type: "error",
            });
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogContent sx={{ padding: theme.spacing(4) }}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    marginBottom={theme.spacing(3)}
                >
                    <Typography variant="h3">
                        {definition ?
                            t("settings.cohorts.edit_title", "Edit cohort definition")
                        :   t("settings.cohorts.create_title", "New cohort definition")}
                    </Typography>
                    <FoodsIconButton
                        size="s"
                        icon="close"
                        tooltip={{ text: t("common.close", "Close") }}
                        buttonProps={{ onClick: onClose }}
                    />
                </Stack>

                <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                    <Stack gap={theme.spacing(2.5)}>
                        <Controller
                            name="code"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t("settings.cohorts.field.code", "Code")}
                                    disabled={Boolean(definition)}
                                    error={Boolean(errors.code)}
                                    helperText={errors.code?.message}
                                    fullWidth
                                />
                            )}
                        />
                        <Controller
                            name="label"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t("settings.cohorts.field.label", "Label")}
                                    error={Boolean(errors.label)}
                                    helperText={errors.label?.message}
                                    fullWidth
                                />
                            )}
                        />
                        <Controller
                            name="entityClass"
                            control={control}
                            render={({ field }) => (
                                <Stack gap={theme.spacing(0.5)}>
                                    <Typography variant="small">
                                        {t("settings.cohorts.field.entity_class", "Applies to")}
                                    </Typography>
                                    <FoodsSelect
                                        selectedOption={
                                            entityClassOptions.find((option) => option.value === field.value) ?? null
                                        }
                                        options={entityClassOptions}
                                        onChange={(option) => option && field.onChange(option.value)}
                                        getId={(option) => option.value}
                                        getName={(option) => option.label}
                                        closeOnSelect
                                        disableSearch
                                        textOptions={selectTextOptions}
                                    />
                                </Stack>
                            )}
                        />
                        <Controller
                            name="source"
                            control={control}
                            render={({ field }) => (
                                <Stack gap={theme.spacing(0.5)}>
                                    <Typography variant="small">
                                        {t("settings.cohorts.field.source", "Attribute source")}
                                    </Typography>
                                    <FoodsSelect
                                        selectedOption={
                                            sourceOptions.find((option) => option.value === field.value) ?? null
                                        }
                                        options={sourceOptions}
                                        onChange={(option) => option && field.onChange(option.value)}
                                        getId={(option) => option.value}
                                        getName={(option) => option.label}
                                        closeOnSelect
                                        disableSearch
                                        textOptions={selectTextOptions}
                                    />
                                </Stack>
                            )}
                        />
                        <Controller
                            name="attributeKey"
                            control={control}
                            render={({ field }) => (
                                <Stack gap={theme.spacing(0.5)}>
                                    <Typography variant="small">
                                        {t("settings.cohorts.field.attribute_key", "Grouping attribute")}
                                    </Typography>
                                    <FoodsSelect<CohortAttributeChoice>
                                        selectedOption={
                                            attributeChoices.find((choice) => choice.key === field.value) ?? null
                                        }
                                        options={attributeChoices}
                                        onChange={(choice) => field.onChange(choice?.key ?? "")}
                                        getId={(choice) => choice.key}
                                        getName={(choice) => `${choice.label} (${choice.key})`}
                                        closeOnSelect
                                        disabled={isLoadingChoices}
                                        textOptions={selectTextOptions}
                                    />
                                    {errors.attributeKey && (
                                        <Typography variant="small" sx={{ color: theme.palette.error.main }}>
                                            {errors.attributeKey.message}
                                        </Typography>
                                    )}
                                </Stack>
                            )}
                        />
                        <Controller
                            name="minimumPeerCount"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    type="number"
                                    label={t("settings.cohorts.field.minimum_peer_count", "Minimum peer count")}
                                    value={field.value}
                                    onChange={(event) => {
                                        const parsed = Number(event.target.value);
                                        field.onChange(Number.isFinite(parsed) ? parsed : field.value);
                                    }}
                                    error={Boolean(errors.minimumPeerCount)}
                                    helperText={errors.minimumPeerCount?.message}
                                    fullWidth
                                />
                            )}
                        />
                        <Button type="submit" variant="primary" disabled={!isValid || mutation.isLoading}>
                            {t("common.save", "Save")}
                        </Button>
                    </Stack>
                </Box>
            </DialogContent>
        </Dialog>
    );
};
